import { TurnId, type OrchestrationMessage } from "@afar-research/contracts";

/**
 * The sort key a message without an orchestration event sequence takes, so it
 * wins a same-instant tie rather than losing it.
 *
 * The value is what makes the ordering free: it matches where SQLite already
 * puts a NULL in an ascending index, so `ORDER BY created_at, sequence,
 * message_id` is served straight from the ordering index with no sort step.
 * Sequences are non-negative, so nothing collides with it. The cursor
 * comparison spells it as `COALESCE(sequence, <this>)`; keep the two in step.
 */
export const THREAD_MESSAGE_SEQUENCELESS_SORT_KEY = -1;

export interface ThreadMessageOrderFields {
  readonly createdAt: string;
  readonly sequence?: number | null | undefined;
}

/**
 * The durable transcript order for thread messages, shared by every read path.
 *
 * `createdAt` stays the primary key so a message never leaves its place in time.
 * A forked thread imports messages whose originating event sequence is unknown,
 * and sequencing those first or last would move imported history away from the
 * turns around it.
 *
 * The orchestration event `sequence` then breaks the ties `createdAt` cannot.
 * A replayed turn — a turn sent after a Checkpoint Revert, or any burst on a
 * loaded host — lands several messages inside one millisecond, and a message id
 * is random, so without the sequence the order within a burst is arbitrary but
 * persistent.
 *
 * The two SQL reads spell this same rule as `created_at, sequence, message_id`,
 * and the client thread reducer sorts with this comparator, so the order a live
 * thread shows and the order a reloaded one shows cannot disagree. Because the
 * spelling matches the ordering index column for column, including where a NULL
 * sequence sits, neither read pays for a sort.
 */
export const threadMessageOrder =
  <T extends ThreadMessageOrderFields>(idOf: (message: T) => string) =>
  (left: T, right: T): -1 | 0 | 1 => {
    const byCreatedAt = left.createdAt.localeCompare(right.createdAt);
    if (byCreatedAt !== 0) return byCreatedAt < 0 ? -1 : 1;
    const leftSequence = left.sequence ?? THREAD_MESSAGE_SEQUENCELESS_SORT_KEY;
    const rightSequence = right.sequence ?? THREAD_MESSAGE_SEQUENCELESS_SORT_KEY;
    if (leftSequence !== rightSequence) return leftSequence < rightSequence ? -1 : 1;
    const byId = idOf(left).localeCompare(idOf(right));
    return byId === 0 ? 0 : byId < 0 ? -1 : 1;
  };

export function correlateUserMessageWithTurn(
  messages: ReadonlyArray<OrchestrationMessage>,
  input: {
    readonly messageId: string | null | undefined;
    readonly turnId: string | null;
  },
): ReadonlyArray<OrchestrationMessage> {
  if (input.messageId == null || input.turnId === null) return messages;
  const turnId = TurnId.make(input.turnId);
  const target = messages.find((message) => message.id === input.messageId);
  if (target?.role !== "user" || target.turnId !== null) return messages;
  return messages.map((message) =>
    message.id === input.messageId ? { ...message, turnId } : message,
  );
}

export function orderTimelineMessages<
  T extends {
    readonly role: string;
    readonly turnId: string | null;
  },
>(messages: ReadonlyArray<T>): ReadonlyArray<T> {
  const usersByTurnId = new Map<string, T[]>();
  const orderedTurnIds: string[] = [];
  for (const message of messages) {
    if (message.role !== "user") continue;
    if (message.turnId === null) return messages;
    const users = usersByTurnId.get(message.turnId);
    if (users) {
      users.push(message);
    } else {
      usersByTurnId.set(message.turnId, [message]);
      orderedTurnIds.push(message.turnId);
    }
  }
  if (orderedTurnIds.length === 0) return messages;

  const messagesByTurnId = new Map<string, T[]>();
  for (const message of messages) {
    if (
      (message.role !== "user" && message.role !== "assistant") ||
      message.turnId === null ||
      !usersByTurnId.has(message.turnId)
    ) {
      return messages;
    }
    const turnMessages = messagesByTurnId.get(message.turnId);
    if (turnMessages) {
      turnMessages.push(message);
    } else {
      messagesByTurnId.set(message.turnId, [message]);
    }
  }

  const ordered = orderedTurnIds.flatMap((turnId) => messagesByTurnId.get(turnId) ?? []);
  return ordered.every((message, index) => message === messages[index]) ? messages : ordered;
}
